import { apiClient } from './api-client'
import { priorityLabel } from '../lib/publication-inbox'
import { parseDbTimestamp } from '../utils/datetime'
import {
  DJENPublication,
  PublicationFilters,
  PublicationMetrics,
  WeeklyMetrics,
  PublicationNote,
  PublicationAction,
  PublicationPriority
} from '@/types/publication'

export interface PublicationResponse {
  success: boolean
  data: DJENPublication[]
  pagination?: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export interface SinglePublicationResponse {
  success: boolean
  data: DJENPublication
}

export interface MetricsResponse {
  success: boolean
  data: PublicationMetrics
}

export interface WeeklyMetricsResponse {
  success: boolean
  data: WeeklyMetrics
}

export interface NotesResponse {
  success: boolean
  data: PublicationNote[]
}

export class PublicationService {
  static async getPublications(filters: PublicationFilters = {}): Promise<PublicationResponse> {
    const params = new URLSearchParams()
    
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== '' && value !== false) {
        params.append(key, value.toString())
      }
    })

    const response = await apiClient.get(`/api/publications?${params.toString()}`)
    return response.data
  }

  static async getPublicationById(id: number): Promise<SinglePublicationResponse> {
    const response = await apiClient.get(`/api/publications/${id}`)
    return response.data
  }

  static async searchPublications(query: string): Promise<PublicationResponse> {
    const response = await apiClient.get(`/api/publications/search?q=${encodeURIComponent(query)}`)
    return response.data
  }

  static async getMetrics(): Promise<MetricsResponse> {
    const response = await apiClient.get('/api/publications/metrics')
    return response.data
  }

  static async getWeeklyMetrics(weekStart?: string): Promise<WeeklyMetricsResponse> {
    const params = weekStart ? `?weekStart=${weekStart}` : ''
    const response = await apiClient.get(`/api/publications/metrics/weekly${params}`)
    return response.data
  }

  static async getWeeklyComparison<T = Record<string, unknown>>(weekStart?: string): Promise<{ success: boolean; data: T }> {
    const params = weekStart ? `?weekStart=${weekStart}` : ''
    const response = await apiClient.get(`/api/publications/metrics/weekly-comparison${params}`)
    return response.data
  }

  static async getWeeklyTrend<T = Record<string, unknown>>(weeks: number = 4): Promise<{ success: boolean; data: T }> {
    const response = await apiClient.get(`/api/publications/metrics/trend?weeks=${weeks}`)
    return response.data
  }

  static async getMonthlyHeatmap<T = Record<string, unknown>>(year?: number, month?: number): Promise<{ success: boolean; data: T }> {
    const today = new Date()
    const yearParam = year || today.getFullYear()
    const monthParam = month || today.getMonth() + 1
    const response = await apiClient.get(`/api/publications/metrics/heatmap?year=${yearParam}&month=${monthParam}`)
    return response.data
  }

  static async getStatusDistribution<T = Record<string, unknown>>(): Promise<{ success: boolean; data: T }> {
    const response = await apiClient.get('/api/publications/metrics/distribution')
    return response.data
  }

  static async getTopTribunals<T = Record<string, unknown>>(limit: number = 5, weekStart?: string): Promise<{ success: boolean; data: T }> {
    const params = new URLSearchParams()
    params.append('limit', limit.toString())
    if (weekStart) {
      params.append('weekStart', weekStart)
    }
    const response = await apiClient.get(`/api/publications/metrics/top-tribunals?${params.toString()}`)
    return response.data
  }

  static async getProcessingFunnel<T = Record<string, unknown>>(weekStart?: string): Promise<{ success: boolean; data: T }> {
    const params = weekStart ? `?weekStart=${weekStart}` : ''
    const response = await apiClient.get(`/api/publications/metrics/funnel${params}`)
    return response.data
  }

  static async lockPublication(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/lock`)
    return response.data
  }

  static async unlockPublication(id: number): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/unlock`)
    return response.data
  }

  static async markAsRelevant(id: number, reason: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/mark-relevant`, { reason })
    return response.data
  }

  static async markAsIrrelevant(id: number, reason: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/mark-irrelevant`, { reason })
    return response.data
  }

  static async assignUser(id: number, userId: string): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/assign`, { userId })
    return response.data
  }

  static async setPriority(id: number, priority: PublicationPriority): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/priority`, { priority })
    return response.data
  }

  static async addNote(id: number, noteText: string, noteType: string = 'GENERAL'): Promise<{ success: boolean }> {
    const response = await apiClient.post(`/api/publications/${id}/notes`, { noteText, noteType })
    return response.data
  }

  static async getNotes(id: number): Promise<NotesResponse> {
    const response = await apiClient.get(`/api/publications/${id}/notes`)
    return response.data
  }

  static async markAsReviewed(id: number): Promise<{ success: boolean; message: string }> {
    const response = await apiClient.post(`/api/publications/${id}/mark-reviewed`)
    return response.data
  }

  static async bulkActions(actions: PublicationAction): Promise<{ success: boolean; results: Array<Record<string, unknown>> }> {
    const response = await apiClient.post('/api/publications/bulk-actions', actions)
    return response.data
  }

  static async importFromDJEN(publicationData: Partial<DJENPublication>): Promise<SinglePublicationResponse> {
    const response = await apiClient.post('/api/publications/import', publicationData)
    return response.data
  }

  static formatDate(dateString: string): string {
    try {
      const date = parseDbTimestamp(dateString)
      if (!date) return dateString
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    } catch {
      return dateString
    }
  }

  /** Classes de selo do status, só com tokens do tema (visual v2). */
  static getStatusColor(status: string): string {
    const colors = {
      'PENDING': 'border-border bg-background text-foreground',
      'ANALYZING': 'bg-secondary text-secondary-foreground',
      'ANALYZED': 'bg-primary/10 text-primary',
      'AUTO_LINKED': 'bg-primary/10 text-primary',
      'CASE_LINKED': 'bg-primary text-primary-foreground',
      'TASK_CREATED': 'bg-primary text-primary-foreground',
      'ARCHIVED': 'bg-muted text-muted-foreground'
    }
    return colors[status as keyof typeof colors] || 'bg-muted text-foreground'
  }

  static getStatusLabel(status: string): string {
    const labels = {
      'PENDING': 'Pendente',
      'ANALYZING': 'Analisando',
      'ANALYZED': 'Analisada',
      'AUTO_LINKED': 'Auto-vinculada',
      'CASE_LINKED': 'Vinculada a Processo',
      'TASK_CREATED': 'Tarefa Criada',
      'ARCHIVED': 'Arquivada'
    }
    return labels[status as keyof typeof labels] || status
  }

  /** Classes de selo da prioridade, só com tokens do tema (visual v2). */
  static getPriorityColor(priority: string): string {
    const colors = {
      'LOW': 'bg-muted text-muted-foreground',
      'MEDIUM': 'bg-secondary text-secondary-foreground',
      'HIGH': 'border-border bg-background font-semibold text-foreground',
      'URGENT': 'bg-destructive/10 text-destructive'
    }
    return colors[priority as keyof typeof colors] || 'bg-muted text-foreground'
  }

  static getPriorityLabel(priority: string): string {
    return priorityLabel(priority)
  }

  static truncateText(text: string | null | undefined, maxLength: number = 200): string {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '...'
  }

  /**
   * Get quick filter counts
   */
  static async getQuickFilterCounts(): Promise<{
    success: boolean
    data: {
      needsAction: number
      inProcess: number
      done: number
      total: number
    }
  }> {
    const response = await apiClient.get('/api/publications/quick-filters/counts')
    return response.data
  }

  /**
   * Link publication to case
   */
  static async linkToCase(
    publicationId: number,
    caseId: string
  ): Promise<{ success: boolean; data: DJENPublication }> {
    const response = await apiClient.patch(
      `/api/publications/${publicationId}/link-case`,
      { caseId }
    );
    return response.data;
  }
}
